import React, { useState, useMemo, useEffect } from "react";
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ArrowUpDown, Download, Receipt } from "lucide-react";
import { useTransactions } from "@/hooks/use-transactions";
import { formatCurrency } from "@/lib/utils/format";
import { exportTransactionsToCsv } from "@/lib/utils/csv-export";
import { t } from "@/lib/i18n/messages";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonTable } from "@/components/ui/skeleton";
import { ErrorMessage } from "@/components/ui/error-message";
import type { Locale } from "@/types/auth";
import type { Transaction, TransactionType, TransactionStatus } from "@/types/wallet";

// ── Types ─────────────────────────────────────────────────────────────────────

interface TransactionsTableProps {
  locale: Locale;
  tableRef?: React.RefObject<HTMLDivElement>;
  highlightedId?: string;
  filterType?: TransactionType;
}

type QuickFilter = "all" | "inbound" | "outbound" | "settled" | "pending" | "failed";
type SortField = "created_at" | "total_amount";
type SortDirection = "asc" | "desc";

interface SortState {
  field: SortField | null;
  direction: SortDirection;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const TRANSACTION_TYPES: (TransactionType | "all")[] = [
  "all", "Fund", "NcTransfer", "CpEarn", "GpEarn", "SpEarn",
  "CpExchange", "GpExchange", "SpExchange",
];

const TRANSACTION_STATUSES: (TransactionStatus | "all")[] = [
  "all", "pending", "completed", "failed",
];

const QUICK_FILTERS: { key: QuickFilter; labelKey: string }[] = [
  { key: "all", labelKey: "quickFilterAll" },
  { key: "inbound", labelKey: "quickFilterInbound" },
  { key: "outbound", labelKey: "quickFilterOutbound" },
  { key: "settled", labelKey: "quickFilterSettled" },
  { key: "pending", labelKey: "quickFilterPending" },
  { key: "failed", labelKey: "quickFilterFailed" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function getStatusVariant(status: TransactionStatus) {
  switch (status) {
    case "completed": return "success" as const;
    case "pending": return "warning" as const;
    case "failed": return "error" as const;
    default: return "default" as const;
  }
}

function getDirectionColor(direction: string) {
  return direction === "Outbound" ? "text-error" : "text-success";
}

function formatLocalDate(dateString: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(dateString));
}

function applyQuickFilter(data: Transaction[], filter: QuickFilter): Transaction[] {
  switch (filter) {
    case "inbound": return data.filter((t) => t.direction === "Inbound");
    case "outbound": return data.filter((t) => t.direction === "Outbound");
    case "settled": return data.filter((t) => t.settled === true);
    // pending/failed are handled via API (setStatus) — show full data when selected
    default: return data;
  }
}

function applySort(data: Transaction[], sort: SortState): Transaction[] {
  if (!sort.field) return data;
  return [...data].sort((a, b) => {
    let cmp = 0;
    if (sort.field === "created_at") {
      cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    } else if (sort.field === "total_amount") {
      cmp = parseFloat(a.total_amount) - parseFloat(b.total_amount);
    }
    return sort.direction === "asc" ? cmp : -cmp;
  });
}

// ── SortIcon ──────────────────────────────────────────────────────────────────

function SortIcon({ field, sort }: { field: SortField; sort: SortState }) {
  if (sort.field !== field) return <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />;
  return sort.direction === "asc"
    ? <ChevronUp className="h-3.5 w-3.5" />
    : <ChevronDown className="h-3.5 w-3.5" />;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function TransactionsTable({ locale, highlightedId, filterType }: TransactionsTableProps) {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [quickFilter, setQuickFilter] = useState<QuickFilter>("all");
  const [sort, setSort] = useState<SortState>({ field: null, direction: "asc" });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const {
    data,
    loading,
    error,
    meta,
    refetch,
    setPage,
    setType,
    setStatus,
    currentParams,
  } = useTransactions({ initialPerPage: 10 });

  // Sync external filterType prop (e.g. from stat card click on dashboard)
  useEffect(() => {
    setType(filterType);
    setQuickFilter("all");
  }, [filterType, setType]);

  const isRTL = locale === "ar";

  // Apply client-side quick filter + sort
  const displayData = useMemo(() => {
    const filtered = applyQuickFilter(data, quickFilter);
    return applySort(filtered, sort);
  }, [data, quickFilter, sort]);

  const handleTypeChange = (type: string) => {
    setType(type === "all" ? undefined : (type as TransactionType));
  };

  const handleStatusChange = (status: string) => {
    setStatus(status === "all" ? undefined : (status as TransactionStatus));
  };

  const handleQuickFilter = (filter: QuickFilter) => {
    setQuickFilter(filter);
    setSelectedIds(new Set());
    // Pending/failed need API filtering
    if (filter === "pending") setStatus("pending");
    else if (filter === "failed") setStatus("failed");
    else setStatus(undefined);
  };

  const handleSort = (field: SortField) => {
    setSort((prev) =>
      prev.field === field
        ? { field, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { field, direction: "asc" }
    );
  };

  const toggleRow = (id: string) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  // Checkbox selection
  const allVisibleIds = displayData.map((t) => t.id);
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedIds.has(id));
  const someSelected = allVisibleIds.some((id) => selectedIds.has(id)) && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allVisibleIds));
    }
  };

  const toggleSelectRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // CSV export
  const handleExport = (selectedOnly: boolean) => {
    const rows = selectedOnly
      ? displayData.filter((tx) => selectedIds.has(tx.id))
      : displayData;
    exportTransactionsToCsv(rows, locale);
  };

  if (loading && data.length === 0) return <SkeletonTable rows={5} />;

  if (error && data.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-8">
        <ErrorMessage
          title={t(locale, "error")}
          message={error}
          onRetry={refetch}
          retryLabel={t(locale, "retry")}
        />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden relative">
      {/* Header */}
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h2 className="text-lg md:text-xl font-bold text-foreground">
            {t(locale, "transactions")}
          </h2>

          {/* Export buttons */}
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <button
                type="button"
                onClick={() => handleExport(true)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium",
                  "bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                )}
              >
                <Download className="h-4 w-4" />
                {t(locale, "exportSelected")}
              </button>
            )}
            <button
              type="button"
              onClick={() => handleExport(false)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium",
                "border border-border hover:bg-muted/50 text-foreground transition-colors"
              )}
            >
              <Download className="h-4 w-4" />
              {t(locale, "exportCsv")}
            </button>
          </div>
        </div>

        {/* Quick filter chips */}
        <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label={t(locale, "filter")}>
          {QUICK_FILTERS.map(({ key, labelKey }) => (
            <button
              key={key}
              type="button"
              onClick={() => handleQuickFilter(key)}
              className={cn(
                "px-3 py-1 rounded-full text-sm font-medium transition-colors duration-150",
                quickFilter === key
                  ? "bg-primary text-primary-foreground"
                  : "border border-border text-foreground-secondary hover:border-primary hover:text-foreground"
              )}
            >
              {t(locale, labelKey as Parameters<typeof t>[1])}
            </button>
          ))}
        </div>

        {/* Type + Status dropdowns */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <label htmlFor="type-filter" className="sr-only">
              {t(locale, "filter")} {t(locale, "type")}
            </label>
            <select
              id="type-filter"
              value={currentParams.type || "all"}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="w-full px-4 py-2.5 pe-10 bg-muted border border-border rounded-[var(--radius-sm)] focus:ring-2 focus:ring-primary/40 focus:border-primary text-sm text-foreground appearance-none bg-none transition-colors duration-base"
            >
              {TRANSACTION_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type === "all" ? t(locale, "all") : type}
                </option>
              ))}
            </select>
            <span className="material-icons-outlined absolute top-1/2 -translate-y-1/2 end-3 pointer-events-none text-gray-400 text-lg">
              expand_more
            </span>
          </div>

          <div className="relative flex-1">
            <label htmlFor="status-filter" className="sr-only">
              {t(locale, "filter")} {t(locale, "status")}
            </label>
            <select
              id="status-filter"
              value={currentParams.status || "all"}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="w-full px-4 py-2.5 pe-10 bg-muted border border-border rounded-[var(--radius-sm)] focus:ring-2 focus:ring-primary/40 focus:border-primary text-sm text-foreground appearance-none bg-none transition-colors duration-base"
            >
              {TRANSACTION_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status === "all" ? t(locale, "all") : t(locale, status)}
                </option>
              ))}
            </select>
            <span className="material-icons-outlined absolute top-1/2 -translate-y-1/2 end-3 pointer-events-none text-gray-400 text-lg">
              expand_more
            </span>
          </div>
        </div>
      </div>

      {displayData.length === 0 ? (
        <div className="p-8">
          <EmptyState icon={Receipt} title={t(locale, "noData")} description={t(locale, "noDataAvailable")} />
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full" role="table" aria-label={t(locale, "transactions")}>
              <thead className="sticky top-0 z-10 bg-muted/50 text-foreground-secondary text-sm font-medium border-b border-border">
                <tr>
                  {/* Select all checkbox */}
                  <th scope="col" className="px-4 py-3.5 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleSelectAll}
                      className="rounded border-border text-primary focus:ring-primary"
                      aria-label="Select all"
                    />
                  </th>
                  <th scope="col" className="px-6 py-3.5 text-start">
                    <button
                      type="button"
                      onClick={() => handleSort("created_at")}
                      className="flex items-center gap-1 hover:text-foreground transition-colors"
                    >
                      {t(locale, "date")}
                      <SortIcon field="created_at" sort={sort} />
                    </button>
                  </th>
                  <th scope="col" className="px-6 py-3.5 text-start">{t(locale, "type")}</th>
                  <th scope="col" className="px-6 py-3.5 text-start">
                    <button
                      type="button"
                      onClick={() => handleSort("total_amount")}
                      className="flex items-center gap-1 hover:text-foreground transition-colors"
                    >
                      {t(locale, "amount")}
                      <SortIcon field="total_amount" sort={sort} />
                    </button>
                  </th>
                  <th scope="col" className="px-6 py-3.5 text-start">{t(locale, "status")}</th>
                  <th scope="col" className="px-6 py-3.5 text-start">{t(locale, "initiative")}</th>
                  <th scope="col" className="px-6 py-3.5">
                    <span className="sr-only">{t(locale, "actions")}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {displayData.map((transaction, idx) => {
                  const isHighlighted = highlightedId === transaction.id;
                  return (
                    <React.Fragment key={transaction.id ?? `tx-${idx}`}>
                      <tr
                        className={cn(
                          "hover:bg-muted/30 transition-colors duration-300 cursor-pointer",
                          isHighlighted && "bg-primary/10"
                        )}
                        onClick={() => toggleRow(transaction.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleRow(transaction.id);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-expanded={expandedRow === transaction.id}
                      >
                        {/* Row checkbox */}
                        <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(transaction.id)}
                            onChange={() => toggleSelectRow(transaction.id)}
                            className="rounded border-border text-primary focus:ring-primary"
                            aria-label={`Select ${transaction.id}`}
                          />
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground whitespace-nowrap">
                          {formatLocalDate(transaction.created_at, locale)}
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground">{transaction.type}</td>
                        <td className={cn("px-6 py-4 text-sm font-semibold tabular-nums", getDirectionColor(transaction.direction))}>
                          {formatCurrency(transaction.amount, locale)}
                        </td>
                        <td className="px-6 py-4">
                          <Badge variant={getStatusVariant(transaction.status)}>
                            {t(locale, transaction.status)}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground">
                          {transaction.initiative?.title || "-"}
                        </td>
                        <td className="px-6 py-4">
                          {expandedRow === transaction.id
                            ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                            : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          }
                        </td>
                      </tr>
                      {expandedRow === transaction.id && (
                        <tr id={`details-${transaction.id}`}>
                          <td colSpan={7} className="px-6 py-4 bg-muted/30">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="font-medium text-foreground-secondary">{t(locale, "transactionId")}:</span>{" "}
                                <span className="text-foreground font-mono text-xs">{transaction.id}</span>
                              </div>
                              <div>
                                <span className="font-medium text-foreground-secondary">{t(locale, "direction")}:</span>{" "}
                                <span className="text-foreground">{transaction.direction}</span>
                              </div>
                              {transaction.fee && (
                                <div>
                                  <span className="font-medium text-foreground-secondary">{t(locale, "fee")}:</span>{" "}
                                  <span className="text-foreground">{formatCurrency(transaction.fee, locale)}</span>
                                </div>
                              )}
                              {transaction.commission && (
                                <div>
                                  <span className="font-medium text-foreground-secondary">{t(locale, "commission")}:</span>{" "}
                                  <span className="text-foreground">{formatCurrency(transaction.commission, locale)}</span>
                                </div>
                              )}
                              <div>
                                <span className="font-medium text-foreground-secondary">{t(locale, "settled")}:</span>{" "}
                                <span className="text-foreground">{transaction.settled ? t(locale, "yes") : t(locale, "no")}</span>
                              </div>
                              {transaction.initiative && (
                                <div className="md:col-span-2">
                                  <span className="font-medium text-foreground-secondary">{t(locale, "initiativeType")}:</span>{" "}
                                  <span className="text-foreground">{transaction.initiative.type}</span>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-border">
            {displayData.map((transaction, idx) => {
              const isHighlighted = highlightedId === transaction.id;
              return (
                <div
                  key={transaction.id ?? `tx-${idx}`}
                  className={cn(
                    "p-4 transition-colors duration-300",
                    isHighlighted ? "bg-primary/10" : "hover:bg-muted/30"
                  )}
                  onClick={() => toggleRow(transaction.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleRow(transaction.id);
                    }
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-foreground">{transaction.type}</span>
                    <Badge variant={getStatusVariant(transaction.status)}>
                      {t(locale, transaction.status)}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={cn("text-lg font-bold tabular-nums", getDirectionColor(transaction.direction))}>
                      {formatCurrency(transaction.amount, locale)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatLocalDate(transaction.created_at, locale)}
                    </span>
                  </div>
                  {transaction.initiative?.title && (
                    <p className="text-xs text-foreground-secondary mt-1">{transaction.initiative.title}</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {meta && meta.last_page > 1 && (
            <div className="p-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-sm text-foreground-secondary">
                {t(locale, "showing")}{" "}
                {(meta.current_page - 1) * meta.per_page + 1} -{" "}
                {Math.min(meta.current_page * meta.per_page, meta.total)}{" "}
                {t(locale, "of")} {meta.total} {t(locale, "results")}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(meta.current_page - 1)}
                  disabled={meta.current_page === 1 || loading}
                  className="p-2 rounded-lg border border-border text-foreground-secondary hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-base"
                  aria-label={t(locale, "previous")}
                >
                  {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                </button>
                <span className="text-sm text-foreground tabular-nums">
                  {meta.current_page} / {meta.last_page}
                </span>
                <button
                  onClick={() => setPage(meta.current_page + 1)}
                  disabled={meta.current_page === meta.last_page || loading}
                  className="p-2 rounded-lg border border-border text-foreground-secondary hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-base"
                  aria-label={t(locale, "next")}
                >
                  {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              </div>
            </div>
          )}

          {loading && (
            <div className="absolute inset-0 bg-card/50 flex items-center justify-center">
              <div className="w-8 h-8 border-[3px] border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
